import classnames from "classnames";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import FacebookIcon from "../../icons/FacebookIcon";
import LinkedInIcon from "../../icons/LinkedInIcon";
import { useDeploymentDetails } from "../../model/deployment";
import { useSocialConfigData } from "../../model/social";
import { useGatsbyLocation } from "../../utils/useGatsbyLocation";
import { usePageMetadata } from "../../utils/usePageMetadata";
import Link from "../Link/Link";
import Text, { TextVariant } from "../Text/Text";
import styles from "./SocialLinks.module.scss";

interface SocialLinksProps {
  className?: string;
  // Unused since we dropped Twitter/X but potentially useful in future.
  title: string;
  // The white version is larger and copes with a background image.
  type?: "normal" | "white";
  scale?: number;
  textVariant?: TextVariant;
}

const SocialLinks = ({
  className,
  type = "normal",
  textVariant = "default",
  scale = 1
}: SocialLinksProps) => {
  const { pathname: url } = useGatsbyLocation();
  const deployment = useDeploymentDetails();
  const { canonicalUrlOverride } = usePageMetadata();
  const encodedPageUrl = encodeURIComponent(
    `${deployment.url}${canonicalUrlOverride || url}`
  );
  const facebookLink = `https://www.facebook.com/sharer/sharer.php?u=${encodedPageUrl}`;
  const linkedInLink = `https://www.linkedin.com/shareArticle?url=${encodedPageUrl}`;
  const { facebook, linkedin } = useSocialConfigData();
  const intl = useIntl();
  const shareText = intl.formatMessage({ id: "share", defaultMessage: "Share" });
  return (
    <div className={classnames(styles.root, className, styles[type])}>
      <Text className={styles.shareTitle} variant={textVariant} as="span">
        <FormattedMessage id="share" defaultMessage="Share" />
      </Text>
      <Link
        aria-label={`Facebook ${shareText}`}
        to={facebookLink || `https://www.facebook.com/${facebook.handle}`}
      >
        <FacebookIcon height={20 * scale} width={20 * scale} />
      </Link>
      <Link
        aria-label={`LinkedIn ${shareText}`}
        to={
          linkedInLink || `https://www.linkedin.com/company/${linkedin.handle}`
        }
      >
        <LinkedInIcon height={20 * scale} width={20 * scale} />
      </Link>
    </div>
  );
};

export default SocialLinks;
